'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import axios from 'src/utils/axios';
import { isValidEmail, getEmailRoutingAction } from 'src/utils/emailValidation';

interface WaitlistWidgetProps {
  className?: string;
}

interface WaitlistResponse {
  message: string;
  id?: string;
}

export default function WaitlistWidget({ className = '' }: WaitlistWidgetProps) {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const joinWaitlistMutation = useMutation<WaitlistResponse, Error, { email: string; source: string }>({
    mutationFn: async (data) => {
      return axios.post<WaitlistResponse>('/v1/waitlist', data);
    },
    onSuccess: (data) => {
      setIsSubmitted(true);
      setSuccessMessage(data.message || 'Successfully joined the waitlist!');
      setEmail('');
      setShowValidation(false);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    
    if (!email) {
      return;
    }

    if (!isValidEmail(email)) {
      return;
    }

    const routingAction = getEmailRoutingAction(email);
    
    if (routingAction === 'login') {
      // Redirect to login page for @arohalabs.com emails
      router.push('/login');
      return;
    }
    
    // Submit to waitlist for all other emails
    joinWaitlistMutation.mutate({ 
      email,
      source: 'landing_page'
    });
  };

  // Validation error handling
  const getError = () => {
    if (showValidation && !email) {
      return 'Email is required';
    }
    if (showValidation && email && !isValidEmail(email)) {
      return 'Please enter a valid email address';
    }
    if (joinWaitlistMutation.error) {
      return joinWaitlistMutation.error.message || 'Failed to join waitlist. Please try again.';
    }
    return null;
  };

  const error = getError();

  if (isSubmitted && successMessage) {
    return (
      <div className={`border border-green-500/30 rounded-md p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <svg className="h-5 w-5 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm text-green-400 font-medium">Successfully joined!</p>
            <p className="text-xs text-gray-400 mt-0.5">{successMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            id="widget-email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (showValidation && e.target.value) {
                setShowValidation(false);
              }
            }}
            placeholder="Enter your email"
            disabled={joinWaitlistMutation.isPending}
            className="flex-1 px-4 py-2.5 bg-white/5 border border-white/20 rounded-md text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-white/40 focus:border-white/40 focus:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          />
          <button
            type="submit"
            disabled={joinWaitlistMutation.isPending}
            className="bg-white text-black px-6 py-2.5 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center whitespace-nowrap"
          >
            {joinWaitlistMutation.isPending ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Joining...</span>
              </>
            ) : (
              'Join Waitlist'
            )}
          </button>
        </div>
        {error && (
          <p className="text-xs text-red-400 mt-1">{error}</p>
        )}
      </form>
      
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-400">
          Already have an account?{' '}
          <button
            onClick={() => router.push('/login')}
            className="text-white hover:text-gray-200 underline underline-offset-2 transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}